import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Avatar,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Fab,
  Card,
  CardContent,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Input,
  Tabs,
  Tab,
  Snackbar,
  useMediaQuery,
  useTheme,
  Stack,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  Search as SearchIcon,
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Autorenew as RenewalIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon,
  Email as EmailIcon,
  Payment as PaymentIcon,
  CloudUpload as UploadIcon,
  Check as CheckIcon,
  Schedule as ScheduleIcon,
  PriorityHigh as PriorityIcon,
  Person as PersonIcon,
  AttachMoney as MoneyIcon,
  Receipt as ReceiptIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  FilterList as FilterIcon,
  Close as CloseIcon,
  ContentCopy as ContentCopyIcon,
  DeviceHub as DeviceIcon,
  DirectionsCar as CarIcon,
  History as HistoryIcon,
  CheckCircle as CheckCircleIcon,
  AttachFile as AttachFileIcon,
  ContactSupport as ContactSupportIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { useApi, useAsyncOperation } from '../../hooks/useApi';
import { apiService, urlDeArchivo } from '../../services/api';
import { Renewal, RenewalWithDetails, RenewalsApiResponse, RenewalPaymentForm } from '../../types';
import { formatDate, formatCurrency, getDaysUntilExpiration, getPriorityColor, getRenewalPriority, getStatusColor, getDeviceDisplayName, getDeviceBrandModel } from '../../utils/helpers';
import { useNavigate } from 'react-router-dom';
import ContactLogDialog from '../../components/renewals/ContactLogDialog';
import { Accordion, AccordionSummary, AccordionDetails, Badge, LinearProgress } from '@mui/material';
import { ExpandMore as ExpandMoreIcon, Layers as LayersIcon, TrendingUp as TrendingUpIcon } from '@mui/icons-material';

// Tipos para agrupación
interface GroupedRenewal {
  clientId: string;
  clientName: string;
  clientEmail?: string;
  clientPhone?: string;
  renewals: RenewalWithDetails[];
  count: number;
  earliestDueDate: Date;
  latestDueDate: Date;
  totalAmount: number;
  highestPriority: string;
  expiredCount: number;
  upcomingCount: number;
}

interface MonthGroup {
  monthKey: string;
  monthLabel: string;
  renewals: RenewalWithDetails[];
}

interface RenewalRowProps {
  renewal: RenewalWithDetails;
  onView: (renewal: RenewalWithDetails) => void;
  onContact: (renewal: RenewalWithDetails) => void;
  onPayment: (renewal: RenewalWithDetails) => void;
  onUpdateStatus: (renewal: RenewalWithDetails, status: Renewal['status']) => void;
  onCancelRenewal?: (renewal: RenewalWithDetails) => void;
  onViewContactHistory?: (renewal: RenewalWithDetails) => void;
}

const RenewalRow: React.FC<RenewalRowProps> = ({ renewal, onView, onContact, onPayment, onUpdateStatus, onCancelRenewal, onViewContactHistory }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const daysUntil = getDaysUntilExpiration(renewal.dueDate);
  const priority = getRenewalPriority(daysUntil);
  const priorityColor = getPriorityColor(priority);

  const getDaysLabel = (days: number) => {
    if (days < 0) return `Vencido hace ${Math.abs(days)} días`;
    if (days === 0) return 'Vence hoy';
    if (days === 1) return 'Vence mañana';
    return `Vence en ${days} días`;
  };

  const getPriorityLabel = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'Urgente';
      case 'high': return 'Alta';
      case 'medium': return 'Media';
      case 'low': return 'Baja';
      default: return priority;
    }
  };

  return (
    <TableRow hover sx={{ borderLeft: `4px solid ${priorityColor}` }}>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: priorityColor }}>
            <PersonIcon />
          </Avatar>
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 500 }}>
              {renewal.clientId.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Contrato: {renewal.contractId.contractNumber}
            </Typography>
          </Box>
        </Box>
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: priorityColor,
            }}
          />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {getPriorityLabel(priority)}
          </Typography>
        </Box>
      </TableCell>
      <TableCell>
        <Typography
          variant="body2"
          color={daysUntil < 0 ? 'error' : daysUntil <= 7 ? 'warning.main' : 'text.secondary'}
          sx={{ fontWeight: daysUntil <= 7 ? 600 : 400 }}
        >
          {getDaysLabel(daysUntil)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {formatDate(renewal.dueDate)}
        </Typography>
      </TableCell>
      <TableCell
        onClick={() => {
          if (renewal.contactHistory && renewal.contactHistory.length > 0) {
            // Si hay contactos, mostrar historial
            onViewContactHistory?.(renewal);
          } else {
            // Si no hay contactos, abrir diálogo de registrar contacto
            onContact(renewal);
          }
        }}
        sx={{
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        {renewal.contactHistory && renewal.contactHistory.length > 0 ? (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {renewal.contactHistory[renewal.contactHistory.length - 1].contactMethod?.toUpperCase()}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatDate(renewal.contactHistory[renewal.contactHistory.length - 1].contactDate!)}
            </Typography>
          </Box>
        ) : (
          <Typography variant="body2" color="primary.main" sx={{ fontWeight: 500 }}>
            Sin contactar
          </Typography>
        )}
      </TableCell>
      <TableCell>
        {renewal.paymentInfo?.amount ? (
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 500, color: 'success.main' }}>
              {formatCurrency(renewal.paymentInfo.amount)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatDate(renewal.paymentInfo.paymentDate!)}
            </Typography>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">
            Sin pago
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <IconButton onClick={handleMenuOpen} size="small">
          <MoreVertIcon />
        </IconButton>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
          <MenuItem onClick={() => { onView(renewal); handleMenuClose(); }}>
            <ViewIcon sx={{ mr: 1 }} fontSize="small" />
            Ver Detalles
          </MenuItem>
          <MenuItem onClick={() => { onContact(renewal); handleMenuClose(); }}>
            <ContactSupportIcon sx={{ mr: 1 }} fontSize="small" />
            Registrar Contacto
          </MenuItem>
          {renewal.canCreateRenewal && (
            <MenuItem onClick={() => { onPayment(renewal); handleMenuClose(); }}>
              <RenewalIcon sx={{ mr: 1 }} fontSize="small" />
              Procesar Renovación
            </MenuItem>
          )}
          {(renewal.status === 'contacted' || renewal.status === 'payment_pending') && !renewal.canCreateRenewal && (
            <MenuItem onClick={() => { onPayment(renewal); handleMenuClose(); }}>
              <PaymentIcon sx={{ mr: 1 }} fontSize="small" />
              Registrar Pago
            </MenuItem>
          )}
          {renewal.status === 'payment_pending' && (
            <MenuItem onClick={() => { onUpdateStatus(renewal, 'completed'); handleMenuClose(); }}>
              <CheckIcon sx={{ mr: 1 }} fontSize="small" />
              Marcar Completado
            </MenuItem>
          )}
          {onCancelRenewal && (renewal.status === 'pending' || renewal.status === 'contacted' || renewal.status === 'payment_pending') && (
            <>
              <Divider />
              <MenuItem
                onClick={() => {
                  onCancelRenewal(renewal);
                  handleMenuClose();
                }}
                sx={{ color: 'error.main' }}
              >
                <CloseIcon sx={{ mr: 1 }} fontSize="small" />
                Cancelar Renovación
              </MenuItem>
            </>
          )}
        </Menu>
      </TableCell>
    </TableRow>
  );
};

// Componente para filas agrupadas (cliente con múltiples dispositivos)
interface GroupedRenewalRowProps {
  groupedRenewal: GroupedRenewal;
  onOpenDetails: (groupedRenewal: GroupedRenewal) => void;
}

const GroupedRenewalRow: React.FC<GroupedRenewalRowProps> = ({ groupedRenewal, onOpenDetails }) => {
  const priorityColor = getPriorityColor(groupedRenewal.highestPriority);
  const daysUntilEarliest = getDaysUntilExpiration(groupedRenewal.earliestDueDate);

  const getDaysRangeLabel = () => {
    const earliestDays = getDaysUntilExpiration(groupedRenewal.earliestDueDate);
    const latestDays = getDaysUntilExpiration(groupedRenewal.latestDueDate);

    if (earliestDays < 0) {
      return `${groupedRenewal.expiredCount} vencido${groupedRenewal.expiredCount > 1 ? 's' : ''}`;
    }

    if (groupedRenewal.count === 2 && earliestDays === latestDays) {
      return `Vencen en ${earliestDays} días`;
    }

    return `Vencen entre ${earliestDays}-${latestDays} días`;
  };

  const getPriorityLabel = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'Urgente';
      case 'high': return 'Alta';
      case 'medium': return 'Media';
      case 'low': return 'Baja';
      default: return priority;
    }
  };

  return (
    <TableRow
      hover
      sx={{
        borderLeft: `4px solid ${priorityColor}`,
        cursor: 'pointer',
        '&:hover': { backgroundColor: 'action.hover' }
      }}
      onClick={() => onOpenDetails(groupedRenewal)}
    >
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Badge
            badgeContent={groupedRenewal.count}
            color="primary"
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '0.9rem',
                fontWeight: 600,
                minWidth: 24,
                height: 24,
              }
            }}
          >
            <Avatar sx={{ bgcolor: priorityColor }}>
              <LayersIcon />
            </Avatar>
          </Badge>
          <Box>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              {groupedRenewal.clientName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {groupedRenewal.count} dispositivos
              {groupedRenewal.expiredCount > 0 && (
                <Chip
                  label={`${groupedRenewal.expiredCount} vencido${groupedRenewal.expiredCount > 1 ? 's' : ''}`}
                  size="small"
                  color="error"
                  sx={{ ml: 1, height: 18, fontSize: '0.7rem' }}
                />
              )}
            </Typography>
          </Box>
        </Box>
      </TableCell>
      <TableCell>
        <Chip
          label="Múltiples"
          size="small"
          color="info"
          variant="outlined"
        />
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              backgroundColor: priorityColor,
            }}
          />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {getPriorityLabel(groupedRenewal.highestPriority)}
          </Typography>
        </Box>
      </TableCell>
      <TableCell>
        <Typography
          variant="body2"
          color={daysUntilEarliest < 0 ? 'error' : daysUntilEarliest <= 7 ? 'warning.main' : 'text.secondary'}
          sx={{ fontWeight: daysUntilEarliest <= 7 ? 600 : 400 }}
        >
          {getDaysRangeLabel()}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Más próximo: {formatDate(groupedRenewal.earliestDueDate)}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" color="text.secondary">
          Ver detalles
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ fontWeight: 500, color: 'primary.main' }}>
          {formatCurrency(groupedRenewal.totalAmount)}
        </Typography>
      </TableCell>
      <TableCell>
        <Tooltip title="Click para ver detalles">
          <IconButton size="small">
            <ViewIcon />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
};

// Modal para mostrar historial de contactos
interface ContactHistoryModalProps {
  open: boolean;
  renewal: RenewalWithDetails | null;
  onClose: () => void;
  onAddContact: (renewal: RenewalWithDetails) => void;
}

const ContactHistoryModal: React.FC<ContactHistoryModalProps> = ({ open, renewal, onClose, onAddContact }) => {
  if (!renewal) return null;

  const getContactMethodIcon = (method: string) => {
    switch (method) {
      case 'phone': return <PhoneIcon fontSize="small" />;
      case 'whatsapp': return <WhatsAppIcon fontSize="small" />;
      case 'email': return <EmailIcon fontSize="small" />;
      case 'sms': return <PhoneIcon fontSize="small" />;
      default: return <ContactSupportIcon fontSize="small" />;
    }
  };

  const getContactMethodLabel = (method: string) => {
    switch (method) {
      case 'phone': return 'Teléfono';
      case 'whatsapp': return 'WhatsApp';
      case 'email': return 'Email';
      case 'sms': return 'SMS';
      default: return method;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <HistoryIcon color="primary" />
          <Box>
            <Typography variant="h6">Historial de Contactos</Typography>
            <Typography variant="body2" color="text.secondary">
              {renewal.clientId.name} - {renewal.deviceId.name || renewal.deviceId.imei}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {!renewal.contactHistory || renewal.contactHistory.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              No hay contactos registrados para esta renovación
            </Typography>
            <Button
              variant="contained"
              startIcon={<ContactSupportIcon />}
              onClick={() => {
                onAddContact(renewal);
                onClose();
              }}
            >
              Registrar Primer Contacto
            </Button>
          </Box>
        ) : (
          <>
            <Box sx={{ mb: 3 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                Total de contactos: {renewal.contactHistory.length}
              </Alert>
            </Box>

            <List>
              {(renewal.contactHistory || [])
                .slice()
                .reverse()
                .map((contact, index) => {
                  const totalContacts = renewal.contactHistory?.length || 0;
                  return (
                    <React.Fragment key={index}>
                      <ListItem
                        alignItems="flex-start"
                        sx={{
                          bgcolor: index === 0 ? 'action.hover' : 'transparent',
                          borderRadius: 1,
                          mb: 1,
                        }}
                      >
                        <ListItemAvatar>
                          <Avatar
                            sx={{
                              bgcolor: index === 0 ? 'primary.main' : 'grey.400',
                            }}
                          >
                            {getContactMethodIcon(contact.contactMethod || 'phone')}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {getContactMethodLabel(contact.contactMethod || 'phone')}
                              </Typography>
                              {index === 0 && (
                                <Chip
                                  label="Más reciente"
                                  size="small"
                                  color="primary"
                                  sx={{ height: 20 }}
                                />
                              )}
                            </Box>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                {formatDate(contact.contactDate!)} -{' '}
                                {new Date(contact.contactDate!).toLocaleTimeString('es-ES', {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })}
                              </Typography>
                              {contact.response && (
                                <Box sx={{ mb: 1 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Respuesta:
                                  </Typography>
                                  <Typography variant="body2">{contact.response}</Typography>
                                </Box>
                              )}
                              {contact.notes && (
                                <Box sx={{ mb: 1 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    Notas:
                                  </Typography>
                                  <Typography variant="body2">{contact.notes}</Typography>
                                </Box>
                              )}
                              {contact.followUpDate && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                                  <ScheduleIcon fontSize="small" color="warning" />
                                  <Typography variant="caption" color="warning.main">
                                    Seguimiento: {formatDate(contact.followUpDate)}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < totalContacts - 1 && <Divider />}
                    </React.Fragment>
                  );
                })}
            </List>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
        {renewal.contactHistory && renewal.contactHistory.length > 0 && (
          <Button
            variant="contained"
            startIcon={<ContactSupportIcon />}
            onClick={() => {
              onAddContact(renewal);
              onClose();
            }}
          >
            Registrar Nuevo Contacto
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

interface PaymentDialogProps {
  open: boolean;
  renewal: RenewalWithDetails | null;
  onClose: () => void;
  onSubmit: (formData: RenewalPaymentForm) => void;
  loading: boolean;
}

const PaymentDialog: React.FC<PaymentDialogProps> = ({ open, renewal, onClose, onSubmit, loading }) => {
  const [formData, setFormData] = useState<RenewalPaymentForm>({
    amount: 0,
    paymentMethod: '',
    paymentDate: new Date().toISOString().split('T')[0],
    transactionId: '',
    notes: '',
  });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  useEffect(() => {
    if (renewal && open) {
      setFormData({
        amount: renewal.newPeriod?.price || 0,
        paymentMethod: '',
        paymentDate: new Date().toISOString().split('T')[0],
        transactionId: '',
        notes: '',
      });
      setSelectedFile(null);
    }
  }, [renewal, open]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    setSelectedFile(file);
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    onSubmit({
      ...formData,
      evidenceFile: selectedFile || undefined,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          Registrar Pago - {renewal?.clientId.name || 'Cliente'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Monto"
                type="number"
                value={formData.amount}
                onChange={(e) => setFormData(prev => ({ ...prev, amount: parseFloat(e.target.value) }))}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Fecha de Pago"
                type="date"
                value={formData.paymentDate}
                onChange={(e) => setFormData(prev => ({ ...prev, paymentDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <FormControl fullWidth>
                <InputLabel>Método de Pago</InputLabel>
                <Select
                  value={formData.paymentMethod}
                  onChange={(e) => setFormData(prev => ({ ...prev, paymentMethod: e.target.value }))}
                  label="Método de Pago"
                  required
                >
                  <MenuItem value="efectivo">Efectivo</MenuItem>
                  <MenuItem value="transferencia">Transferencia Bancaria</MenuItem>
                  <MenuItem value="tarjeta">Tarjeta de Crédito/Débito</MenuItem>
                  <MenuItem value="cheque">Cheque</MenuItem>
                  <MenuItem value="otros">Otros</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="ID de Transacción / Referencia"
                value={formData.transactionId}
                onChange={(e) => setFormData(prev => ({ ...prev, transactionId: e.target.value }))}
                placeholder="Opcional"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Box>
                <Typography variant="body2" sx={{ mb: 2, fontWeight: 500 }}>
                  Comprobante de Pago
                </Typography>
                <Box
                  sx={{
                    border: '2px dashed',
                    borderColor: 'grey.300',
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    backgroundColor: 'grey.50',
                  }}
                >
                  <input
                    type="file"
                    onChange={handleFileChange}
                    accept="image/*,.pdf"
                    style={{ display: 'none' }}
                    id="payment-evidence"
                  />
                  <label htmlFor="payment-evidence">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<UploadIcon />}
                    >
                      {selectedFile ? 'Cambiar Archivo' : 'Subir Comprobante'}
                    </Button>
                  </label>
                  {selectedFile && (
                    <Typography variant="body2" sx={{ mt: 2, color: 'success.main' }}>
                      📎 {selectedFile.name}
                    </Typography>
                  )}
                  <Typography variant="caption" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
                    Formatos permitidos: JPG, PNG, PDF (Máx. 10MB)
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Notas Adicionales"
                multiline
                rows={3}
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Comentarios opcionales sobre el pago..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || !formData.amount || !formData.paymentMethod}
            startIcon={loading ? <CircularProgress size={20} /> : <PaymentIcon />}
          >
            {loading ? 'Procesando...' : 'Registrar Pago'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

interface DetailsDialogProps {
  open: boolean;
  renewal: RenewalWithDetails | null;
  onClose: () => void;
  onCreateRenewal?: (renewal: RenewalWithDetails) => void;
}

const DetailsDialog: React.FC<DetailsDialogProps> = ({ open, renewal, onClose, onCreateRenewal }) => {
  if (!renewal) return null;

  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handleWhatsApp = (phone: string) => {
    const deviceName = getDeviceDisplayName(renewal.deviceId);
    const message = encodeURIComponent(`Hola, me comunico de Driports respecto a la renovación de su dispositivo GPS "${deviceName}" (IMEI: ${renewal.deviceId.imei}). ¿Podríamos coordinar la renovación?`);
    window.open(`https://wa.me/${phone.replace(/\D/g, '')}?text=${message}`, '_blank');
  };

  const handleEmail = (email: string) => {
    const deviceName = getDeviceDisplayName(renewal.deviceId);
    const subject = encodeURIComponent(`Renovación de Dispositivo GPS - ${deviceName}`);
    const body = encodeURIComponent(`Estimado cliente,

Me comunico de Driports respecto a la renovación de su dispositivo GPS.

Detalles del dispositivo:
- Nombre: ${deviceName}
- IMEI: ${renewal.deviceId.imei}
- Dispositivo: ${getDeviceBrandModel(renewal.deviceId)}
- Fecha de vencimiento: ${formatDate(renewal.dueDate)}

¿Podríamos coordinar la renovación de su servicio?

Saludos cordiales,
Equipo Driports`);
    window.open(`mailto:${email}?subject=${subject}&body=${body}`, '_blank');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <PersonIcon color="primary" />
          <Typography variant="h6">Detalles de Renovación</Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3}>
          {/* Información del Cliente */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <PersonIcon color="primary" />
                  Información del Cliente
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Nombre</Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {renewal.clientId.name}
                    </Typography>
                  </Box>
                  {renewal.clientId.email && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Email</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">{renewal.clientId.email}</Typography>
                        <Tooltip title="Enviar email">
                          <IconButton size="small" onClick={() => handleEmail(renewal.clientId.email!)}>
                            <EmailIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Copiar email">
                          <IconButton size="small" onClick={() => handleCopyToClipboard(renewal.clientId.email!)}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  )}
                  {renewal.clientId.phoneNumber && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Teléfono</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2">{renewal.clientId.phoneNumber}</Typography>
                        <Tooltip title="WhatsApp">
                          <IconButton size="small" onClick={() => handleWhatsApp(renewal.clientId.phoneNumber!)}>
                            <WhatsAppIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Llamar">
                          <IconButton size="small" onClick={() => window.open(`tel:${renewal.clientId.phoneNumber}`)}>
                            <PhoneIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Copiar teléfono">
                          <IconButton size="small" onClick={() => handleCopyToClipboard(renewal.clientId.phoneNumber!)}>
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>
                  )}
                  <Box>
                    <Typography variant="caption" color="text.secondary">Tipo de Cliente</Typography>
                    <Typography variant="body2">{renewal.clientId.clientType || 'N/A'}</Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Información del Dispositivo */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <DeviceIcon color="primary" />
                  Información del Dispositivo
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {renewal.deviceId.name && (
                    <Box>
                      <Typography variant="caption" color="text.secondary">Nombre</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>{renewal.deviceId.name}</Typography>
                    </Box>
                  )}
                  <Box>
                    <Typography variant="caption" color="text.secondary">IMEI</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>{renewal.deviceId.imei}</Typography>
                      <Tooltip title="Copiar IMEI">
                        <IconButton size="small" onClick={() => handleCopyToClipboard(renewal.deviceId.imei)}>
                          <ContentCopyIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Marca/Modelo</Typography>
                    <Typography variant="body2">{getDeviceBrandModel(renewal.deviceId)}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Estado</Typography>
                    <Chip
                      label={renewal.deviceId.status}
                      size="small"
                      color={renewal.deviceId.status === 'active' ? 'success' : 'default'}
                    />
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Fecha de Vencimiento</Typography>
                    <Typography variant="body2" color={getDaysUntilExpiration(renewal.dueDate) <= 7 ? 'error' : 'text.primary'}>
                      {formatDate(renewal.dueDate)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ({getDaysUntilExpiration(renewal.dueDate)} días)
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Información del Vehículo */}
          {renewal.vehicleId && (
            <Grid size={{ xs: 12 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CarIcon color="primary" />
                    Información del Vehículo
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 6, md: 3 }}>
                      <Typography variant="caption" color="text.secondary">Placa</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>{renewal.vehicleId.plate}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, md: 3 }}>
                      <Typography variant="caption" color="text.secondary">Marca</Typography>
                      <Typography variant="body2">{renewal.vehicleId.make}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, md: 3 }}>
                      <Typography variant="caption" color="text.secondary">Modelo</Typography>
                      <Typography variant="body2">{renewal.vehicleId.model}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6, md: 3 }}>
                      <Typography variant="caption" color="text.secondary">Año</Typography>
                      <Typography variant="body2">{renewal.vehicleId.year}</Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Notas */}
          {renewal.notes && (
            <Grid size={{ xs: 12 }}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>Notas</Typography>
                  <Typography variant="body2">{renewal.notes}</Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
        {renewal.canCreateRenewal && onCreateRenewal && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              onCreateRenewal(renewal);
              onClose();
            }}
          >
            Crear Renovación
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// Diálogo para procesar renovación completa
interface ProcessRenewalDialogProps {
  open: boolean;
  renewal: RenewalWithDetails | null;
  onClose: () => void;
  onSuccess: () => void;
}

const ProcessRenewalDialog: React.FC<ProcessRenewalDialogProps> = ({ open, renewal, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    amount: 2500,
    paymentMethod: 'bank_transfer',
    transactionId: '',
    notes: '',
  });
  const [evidenceFile, setEvidenceFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (open && renewal) {
      const deviceName = renewal.deviceId.name || renewal.deviceId.imei;
      setFormData({
        amount: renewal.newPeriod?.price || 2500,
        paymentMethod: 'bank_transfer',
        transactionId: '',
        notes: `Renovación de dispositivo ${deviceName}`,
      });
      setEvidenceFile(null);
      setErrors({});
    }
  }, [open, renewal]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.amount || formData.amount <= 0) {
      newErrors.amount = 'El monto debe ser mayor a 0';
    }

    if (formData.paymentMethod === 'bank_transfer') {
      if (!evidenceFile && !formData.transactionId) {
        newErrors.payment = 'Para transferencias debe adjuntar comprobante o ingresar número de transacción';
      }
    }

    if (formData.paymentMethod === 'cash' && !formData.notes) {
      newErrors.notes = 'Para pagos en efectivo debe incluir detalles del recibo';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm() || !renewal) return;

    setLoading(true);
    try {
      const formDataToSend = new FormData();

      // Extraer el ID real del dispositivo (manejar IDs de candidatos)
      let deviceId = renewal.deviceId._id;
      if (typeof renewal._id === 'string' && renewal._id.startsWith('candidate_')) {
        // Es un candidato, extraer el ID real
        deviceId = renewal._id.replace('candidate_', '');
      }

      formDataToSend.append('deviceId', deviceId);
      formDataToSend.append('amount', formData.amount.toString());
      formDataToSend.append('paymentMethod', formData.paymentMethod);
      if (formData.transactionId) {
        formDataToSend.append('transactionId', formData.transactionId);
      }
      if (formData.notes) {
        formDataToSend.append('notes', formData.notes);
      }
      if (evidenceFile) {
        formDataToSend.append('paymentEvidence', evidenceFile);
      }

      const response = await apiService.processCompleteRenewal(formDataToSend);

      if (response) {
        onSuccess();
        onClose();
      }
    } catch (error) {
      console.error('Error procesando renovación:', error);
      setErrors({ submit: 'Error al procesar la renovación. Por favor intente nuevamente.' });
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        setErrors({ ...errors, file: 'El archivo no debe superar 10MB' });
        return;
      }
      setEvidenceFile(file);
      setErrors({ ...errors, file: '', payment: '' });
    }
  };

  if (!renewal) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <RenewalIcon color="primary" />
          <Typography variant="h6">Procesar Renovación</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          Dispositivo: {renewal.deviceId.name || renewal.deviceId.imei} - Cliente: {renewal.clientId.name}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={3} sx={{ mt: 0 }}>
          {/* Información del dispositivo */}
          <Grid size={{ xs: 12 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              Esta renovación extenderá el servicio por 1 año desde la fecha actual.
            </Alert>
          </Grid>

          {/* Monto */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Monto (DOP)"
              type="number"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) })}
              error={!!errors.amount}
              helperText={errors.amount}
              InputProps={{
                startAdornment: <InputAdornment position="start">RD$</InputAdornment>,
              }}
              required
            />
          </Grid>

          {/* Método de pago */}
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth required error={!!errors.paymentMethod}>
              <InputLabel>Método de Pago</InputLabel>
              <Select
                value={formData.paymentMethod}
                onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                label="Método de Pago"
              >
                <MenuItem value="cash">Efectivo</MenuItem>
                <MenuItem value="bank_transfer">Transferencia</MenuItem>
                <MenuItem value="credit_card">Tarjeta</MenuItem>
                <MenuItem value="check">Cheque</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Número de transacción (para transferencias) */}
          {formData.paymentMethod === 'bank_transfer' && (
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Número de Transacción"
                value={formData.transactionId}
                onChange={(e) => setFormData({ ...formData, transactionId: e.target.value })}
                helperText="Requerido si no adjunta comprobante"
              />
            </Grid>
          )}

          {/* Comprobante de pago */}
          <Grid size={{ xs: 12 }}>
            <Box>
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
                Comprobante de Pago {formData.paymentMethod === 'bank_transfer' && '(Requerido para transferencias)'}
              </Typography>
              <Box
                sx={{
                  border: '2px dashed',
                  borderColor: errors.payment ? 'error.main' : 'grey.300',
                  borderRadius: 2,
                  p: 2,
                  textAlign: 'center',
                  backgroundColor: 'grey.50',
                }}
              >
                <input
                  type="file"
                  onChange={handleFileChange}
                  accept="image/*,.pdf"
                  style={{ display: 'none' }}
                  id="renewal-evidence"
                />
                <label htmlFor="renewal-evidence">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<AttachFileIcon />}
                  >
                    {evidenceFile ? 'Cambiar Archivo' : 'Adjuntar Comprobante'}
                  </Button>
                </label>
                {evidenceFile && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="success.main">
                      ✅ {evidenceFile.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(evidenceFile.size / 1024 / 1024).toFixed(2)} MB
                    </Typography>
                  </Box>
                )}
                {errors.file && (
                  <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                    {errors.file}
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>

          {/* Notas */}
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              label="Notas / Observaciones"
              multiline
              rows={3}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              error={!!errors.notes}
              helperText={errors.notes || 'Requerido para pagos en efectivo'}
              required={formData.paymentMethod === 'cash'}
            />
          </Grid>

          {/* Error general */}
          {errors.payment && (
            <Grid size={{ xs: 12 }}>
              <Alert severity="error">{errors.payment}</Alert>
            </Grid>
          )}
          {errors.submit && (
            <Grid size={{ xs: 12 }}>
              <Alert severity="error">{errors.submit}</Alert>
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <CheckCircleIcon />}
        >
          {loading ? 'Procesando...' : 'Procesar Renovación'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Funciones Helper para agrupación
const groupRenewalsByClient = (renewals: RenewalWithDetails[]): GroupedRenewal[] => {
  const grouped = new Map<string, RenewalWithDetails[]>();

  // Filtrar renovaciones con clientId válido y agrupar
  renewals.forEach(renewal => {
    const clientId = renewal.clientId?._id;
    if (!clientId) return; // Saltar si no hay clientId

    if (!grouped.has(clientId)) {
      grouped.set(clientId, []);
    }
    grouped.get(clientId)!.push(renewal);
  });

  return Array.from(grouped.values())
    .map(clientRenewals => {
      const firstRenewal = clientRenewals[0];
      const dueDates = clientRenewals.map(r => new Date(r.dueDate).getTime());

      // Calcular dispositivos vencidos vs próximos
      const now = new Date().getTime();
      const expiredCount = clientRenewals.filter(r => new Date(r.dueDate).getTime() < now).length;
      const upcomingCount = clientRenewals.length - expiredCount;

      // Determinar prioridad más alta
      const priorities = ['urgent', 'high', 'medium', 'low'];
      const highestPriority = clientRenewals.reduce((highest, renewal) => {
        const days = getDaysUntilExpiration(renewal.dueDate);
        const priority = getRenewalPriority(days);
        const currentIdx = priorities.indexOf(highest);
        const newIdx = priorities.indexOf(priority);
        return newIdx < currentIdx ? priority : highest;
      }, 'low');

      return {
        clientId: firstRenewal.clientId._id || '',
        clientName: firstRenewal.clientId.name,
        clientEmail: firstRenewal.clientId.email || undefined,
        clientPhone: firstRenewal.clientId.phoneNumber || undefined,
        renewals: clientRenewals.sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()),
        count: clientRenewals.length,
        earliestDueDate: new Date(Math.min(...dueDates)),
        latestDueDate: new Date(Math.max(...dueDates)),
        totalAmount: clientRenewals.reduce((sum, r) => sum + (r.newPeriod?.price || 0), 0),
        highestPriority,
        expiredCount,
        upcomingCount,
      };
    })
    .sort((a, b) => a.earliestDueDate.getTime() - b.earliestDueDate.getTime());
};

const groupRenewalsByMonth = (renewals: RenewalWithDetails[]): MonthGroup[] => {
  const grouped = new Map<string, RenewalWithDetails[]>();

  renewals.forEach(renewal => {
    const date = new Date(renewal.dueDate);
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    if (!grouped.has(monthKey)) {
      grouped.set(monthKey, []);
    }
    grouped.get(monthKey)!.push(renewal);
  });

  return Array.from(grouped.entries())
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([monthKey, renewals]) => ({
      monthKey,
      // El mes se arma con números, no parseando '2026-09-01'. Esa cadena JavaScript la lee
      // como medianoche UTC y al escribirla en hora de RD (UTC-4) retrocede al 31 de agosto:
      // el grupo de septiembre salía rotulado "agosto de 2026".
      monthLabel: new Date(Number(monthKey.slice(0, 4)), Number(monthKey.slice(5, 7)) - 1, 1)
        .toLocaleDateString('es-ES', { month: 'long', year: 'numeric' }),
      renewals: renewals.sort((a, b) => new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime())
    }));
};

// Modal para mostrar renovaciones agrupadas por cliente
interface GroupedRenewalsModalProps {
  open: boolean;
  groupedRenewal: GroupedRenewal | null;
  onClose: () => void;
  onProcessRenewal: (renewal: RenewalWithDetails) => void;
  onContact: (renewal: RenewalWithDetails) => void;
  onCancel: (renewal: RenewalWithDetails) => void;
}

const GroupedRenewalsModal: React.FC<GroupedRenewalsModalProps> = ({
  open,
  groupedRenewal,
  onClose,
  onProcessRenewal,
  onContact,
  onCancel
}) => {
  if (!groupedRenewal) return null;

  const monthGroups = groupRenewalsByMonth(groupedRenewal.renewals);
  const now = new Date();

  const getDaysLabel = (days: number) => {
    if (days < 0) return `Vencido hace ${Math.abs(days)} días`;
    if (days === 0) return 'Vence hoy';
    if (days === 1) return 'Vence mañana';
    return `Vence en ${days} días`;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <LayersIcon color="primary" />
          <Box>
            <Typography variant="h6">{groupedRenewal.clientName}</Typography>
            <Typography variant="body2" color="text.secondary">
              {groupedRenewal.count} dispositivo{groupedRenewal.count > 1 ? 's' : ''} -
              {groupedRenewal.expiredCount > 0 && ` ${groupedRenewal.expiredCount} vencido${groupedRenewal.expiredCount > 1 ? 's' : ''}`}
              {groupedRenewal.expiredCount > 0 && groupedRenewal.upcomingCount > 0 && ', '}
              {groupedRenewal.upcomingCount > 0 && ` ${groupedRenewal.upcomingCount} próximo${groupedRenewal.upcomingCount > 1 ? 's' : ''}`}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {/* Información del Cliente */}
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>Información del Cliente</Typography>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}>
                <Typography variant="caption" color="text.secondary">Nombre</Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>{groupedRenewal.clientName}</Typography>
              </Grid>
              {groupedRenewal.clientEmail && (
                <Grid size={{ xs: 12, md: 4 }}>
                  <Typography variant="caption" color="text.secondary">Email</Typography>
                  <Typography variant="body2">{groupedRenewal.clientEmail}</Typography>
                </Grid>
              )}
              {groupedRenewal.clientPhone && (
                <Grid size={{ xs: 12, md: 4 }}>
                  <Typography variant="caption" color="text.secondary">Teléfono</Typography>
                  <Typography variant="body2">{groupedRenewal.clientPhone}</Typography>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>

        {/* Resumen */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, md: 3 }}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h5" color="primary.main" sx={{ fontWeight: 600 }}>
                  {groupedRenewal.count}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Dispositivos
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h5" color="error.main" sx={{ fontWeight: 600 }}>
                  {groupedRenewal.expiredCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Vencidos
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h5" color="warning.main" sx={{ fontWeight: 600 }}>
                  {groupedRenewal.upcomingCount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Próximos
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <Card>
              <CardContent sx={{ textAlign: 'center' }}>
                <Typography variant="h5" color="success.main" sx={{ fontWeight: 600 }}>
                  {formatCurrency(groupedRenewal.totalAmount)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Monto Total
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Dispositivos agrupados por mes */}
        <Typography variant="h6" sx={{ mb: 2 }}>Dispositivos por Mes</Typography>
        {monthGroups.map((monthGroup) => (
          <Accordion key={monthGroup.monthKey} defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 500, textTransform: 'capitalize' }}>
                  {monthGroup.monthLabel}
                </Typography>
                <Chip
                  label={`${monthGroup.renewals.length} dispositivo${monthGroup.renewals.length > 1 ? 's' : ''}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Dispositivo</TableCell>
                      <TableCell>Fecha Vencimiento</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell>Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {monthGroup.renewals.map((renewal) => {
                      const daysUntil = getDaysUntilExpiration(renewal.dueDate);
                      const isExpired = daysUntil < 0;

                      return (
                        <TableRow key={renewal._id} hover>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {getDeviceDisplayName(renewal.deviceId)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                IMEI: {renewal.deviceId.imei}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              color={isExpired ? 'error' : daysUntil <= 7 ? 'warning.main' : 'text.primary'}
                              sx={{ fontWeight: isExpired || daysUntil <= 7 ? 600 : 400 }}
                            >
                              {getDaysLabel(daysUntil)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(renewal.dueDate)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={isExpired ? 'VENCIDO' : 'PRÓXIMO'}
                              size="small"
                              color={isExpired ? 'error' : 'warning'}
                              sx={{ fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Tooltip title="Contactar">
                                <IconButton size="small" onClick={() => onContact(renewal)}>
                                  <PhoneIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Procesar Renovación">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => onProcessRenewal(renewal)}
                                >
                                  <RenewalIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Cancelar Renovación">
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => onCancel(renewal)}
                                >
                                  <CancelIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        ))}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cerrar</Button>
      </DialogActions>
    </Dialog>
  );
};

export const RenewalsPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [currentTab, setCurrentTab] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedRenewal, setSelectedRenewal] = useState<RenewalWithDetails | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [processRenewalOpen, setProcessRenewalOpen] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [completedRenewals, setCompletedRenewals] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [declinedRenewals, setDeclinedRenewals] = useState<any[]>([]);
  const [loadingDeclined, setLoadingDeclined] = useState(false);
  const [groupedModalOpen, setGroupedModalOpen] = useState(false);
  const [selectedGroupedRenewal, setSelectedGroupedRenewal] = useState<GroupedRenewal | null>(null);
  const [contactHistoryModalOpen, setContactHistoryModalOpen] = useState(false);
  const [groupedRowCount, setGroupedRowCount] = useState(0);
  const [forecastData, setForecastData] = useState<any>(null);
  const [loadingForecast, setLoadingForecast] = useState(false);

  const {
    data: renewalsData,
    loading,
    error,
    execute: fetchRenewals,
  } = useApi<RenewalsApiResponse>(
    // ✅ Enviamos page y limit para paginación en backend
    () => apiService.getRenewals(
      page + 1, // TablePagination usa índice 0, backend usa índice 1
      rowsPerPage,
      statusFilter !== 'all' ? statusFilter : undefined,
      priorityFilter !== 'all' ? priorityFilter : undefined
    ),
    { immediate: false }
  );

  const { execute: updateStatus, loading: updating } = useAsyncOperation();
  const { execute: uploadPayment, loading: uploadingPayment } = useAsyncOperation();

  useEffect(() => {
    // Refetch cuando cambian los filtros o la paginación
    fetchRenewals();
  }, [page, rowsPerPage, statusFilter, priorityFilter, fetchRenewals]);

  // Reset page cuando cambian solo los filtros
  useEffect(() => {
    setPage(0);
  }, [statusFilter, priorityFilter]);

  // Actualizar el modal agrupado cuando cambian los datos de renovaciones
  useEffect(() => {
    if (selectedGroupedRenewal && renewalsData?.renewals && groupedModalOpen) {
      const updatedGroups = groupRenewalsByClient(renewalsData.renewals);
      const updatedGroup = updatedGroups.find(g => g.clientId === selectedGroupedRenewal.clientId);

      if (updatedGroup) {
        // Actualizar con los datos nuevos
        setSelectedGroupedRenewal(updatedGroup);
      } else {
        // Ya no hay renovaciones pendientes para este cliente, cerrar modal
        setGroupedModalOpen(false);
        setSelectedGroupedRenewal(null);
      }
    }
  }, [renewalsData]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleView = (renewal: RenewalWithDetails) => {
    setSelectedRenewal(renewal);
    setDetailsDialogOpen(true);
  };

  const handleContact = (renewal: RenewalWithDetails) => {
    setSelectedRenewal(renewal);
    setContactDialogOpen(true);
  };

  const handlePayment = (renewal: RenewalWithDetails) => {
    setSelectedRenewal(renewal);
    setProcessRenewalOpen(true);
  };

  const handleProcessRenewal = (renewal: RenewalWithDetails) => {
    setSelectedRenewal(renewal);
    setProcessRenewalOpen(true);
  };

  const handleOpenGroupedModal = (groupedRenewal: GroupedRenewal) => {
    setSelectedGroupedRenewal(groupedRenewal);
    setGroupedModalOpen(true);
  };

  const handleViewContactHistory = (renewal: RenewalWithDetails) => {
    setSelectedRenewal(renewal);
    setContactHistoryModalOpen(true);
  };

  // Cancelar renovación
  const handleCancelRenewal = async (renewal: RenewalWithDetails) => {
    const deviceName = renewal.deviceId.name || renewal.deviceId.imei;
    const confirmed = window.confirm(
      `¿Está seguro de cancelar la renovación del dispositivo "${deviceName}" del cliente ${renewal.clientId.name}?\n\n` +
      'Esto deshabilitará el dispositivo en Traccar y la SIM card en Simbase.'
    );

    if (!confirmed) return;

    const reason = window.prompt('Razón de la cancelación (opcional):');

    // Si el usuario presionó "Cancelar" en el prompt (reason === null), abortar
    if (reason === null) return;

    try {
      const response = await apiService.cancelRenewal(renewal._id, {
        reason: reason.trim() || undefined,
        notes: 'Cancelado desde interfaz de renovaciones'
      });

      if (response.success || response.partial) {
        setSuccessMessage(
          response.partial
            ? 'Renovación cancelada parcialmente. Revise los errores en la consola.'
            : 'Renovación cancelada exitosamente'
        );
        fetchRenewals();
        if (currentTab === 2) {
          fetchDeclinedRenewals();
        }

        // Mostrar detalles de confirmación
        if (response.confirmations) {
          console.log('📊 Confirmaciones de cancelación:', response.confirmations);
          if (response.confirmations.errors && response.confirmations.errors.length > 0) {
            console.error('❌ Errores durante la cancelación:', response.confirmations.errors);
          }
        }
      } else {
        alert('Error al cancelar la renovación: ' + (response.message || 'Error desconocido'));
      }
    } catch (error: any) {
      console.error('Error cancelando renovación:', error);
      alert('Error al cancelar la renovación: ' + (error.response?.data?.message || error.message));
    }
  };

  // Reactivar renovación
  const handleReactivateRenewal = async (renewal: any) => {
    const deviceName = renewal.deviceId.name || renewal.deviceId.imei;
    const confirmed = window.confirm(
      `¿Está seguro de reactivar la renovación del dispositivo "${deviceName}" del cliente ${renewal.clientId.name}?\n\n` +
      'Esto habilitará el dispositivo en Traccar y la SIM card en Simbase.'
    );

    if (!confirmed) return;

    const notes = window.prompt('Notas adicionales (opcional):');

    try {
      const response = await apiService.reactivateRenewal(renewal._id, {
        notes: notes || undefined
      });

      if (response.success || response.partial) {
        setSuccessMessage(
          response.partial
            ? 'Renovación reactivada parcialmente. Revise los errores en la consola.'
            : 'Renovación reactivada exitosamente'
        );
        fetchRenewals();
        if (currentTab === 2) {
          fetchDeclinedRenewals();
        }

        // Mostrar detalles de confirmación
        if (response.confirmations) {
          console.log('📊 Confirmaciones de reactivación:', response.confirmations);
          if (response.confirmations.errors && response.confirmations.errors.length > 0) {
            console.error('❌ Errores durante la reactivación:', response.confirmations.errors);
          }
        }
      } else {
        alert('Error al reactivar la renovación: ' + (response.message || 'Error desconocido'));
      }
    } catch (error: any) {
      console.error('Error reactivando renovación:', error);
      alert('Error al reactivar la renovación: ' + (error.response?.data?.message || error.message));
    }
  };

  // Cargar historial de renovaciones completadas
  const fetchCompletedRenewals = async () => {
    setLoadingHistory(true);
    try {
      const response = await apiService.getCompletedRenewals();

      if (response?.data?.renewals) {
        setCompletedRenewals(response.data.renewals);
      } else {
        setCompletedRenewals([]);
      }
    } catch (error) {
      console.error('Error cargando historial:', error);
      setCompletedRenewals([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const fetchForecast = async () => {
    setLoadingForecast(true);
    try {
      const response = await apiService.getExpirationForecast();
      if (response?.data) {
        setForecastData(response.data);
      }
    } catch (error) {
      console.error('Error cargando proyecciones:', error);
    } finally {
      setLoadingForecast(false);
    }
  };

  useEffect(() => {
    if (currentTab === 1) {
      fetchCompletedRenewals();
    } else if (currentTab === 2) {
      fetchDeclinedRenewals();
    } else if (currentTab === 3) {
      fetchForecast();
    }
  }, [currentTab]);

  // Cargar renovaciones canceladas
  const fetchDeclinedRenewals = async () => {
    setLoadingDeclined(true);
    try {
      const response = await apiService.getDeclinedRenewals();

      if (response?.data?.renewals) {
        setDeclinedRenewals(response.data.renewals);
      } else {
        setDeclinedRenewals([]);
      }
    } catch (error) {
      console.error('Error cargando renovaciones canceladas:', error);
      setDeclinedRenewals([]);
    } finally {
      setLoadingDeclined(false);
    }
  };

  const handleRenewalSuccess = () => {
    setSuccessMessage('Renovación procesada exitosamente');
    fetchRenewals();
    if (currentTab === 1) {
      fetchCompletedRenewals();
    } else if (currentTab === 2) {
      fetchDeclinedRenewals();
    }
  };

  const handlePaymentSubmit = async (formData: RenewalPaymentForm) => {
    if (!selectedRenewal) return;

    const formDataToSend = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        formDataToSend.append(key, value);
      }
    });

    const result = await uploadPayment(() => apiService.uploadPaymentEvidence(selectedRenewal._id, formDataToSend));
    if (result) {
      setPaymentDialogOpen(false);
      setSelectedRenewal(null);
      fetchRenewals();
    }
  };

  const handleUpdateStatus = async (renewal: RenewalWithDetails, status: Renewal['status']) => {
    const result = await updateStatus(() => apiService.updateRenewalStatus(renewal._id, status));
    if (result) {
      fetchRenewals();
    }
  };

  const getStatusCounts = () => {
    if (!renewalsData?.stats) return { total: 0, pending: 0, contacted: 0, paymentPending: 0, completed: 0, overdue: 0 };

    // Usar las estadísticas globales del backend
    return {
      total: renewalsData.stats.total,
      pending: renewalsData.stats.pending,
      contacted: renewalsData.stats.contacted,
      paymentPending: renewalsData.stats.paymentPending,
      completed: renewalsData.stats.completed,
      overdue: renewalsData.stats.overdue,
    };
  };

  const statusCounts = getStatusCounts();

  if (error) {
    return (
      <Alert severity="error">
        Error cargando renovaciones: {error}
        <Button onClick={() => fetchRenewals()} sx={{ ml: 2 }}>
          Reintentar
        </Button>
      </Alert>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2,
        mb: { xs: 2, md: 4 }
      }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '1.5rem', md: '2.125rem' } }}>
            Gestión de Renovaciones
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
            Administra todas las renovaciones de contratos
          </Typography>
        </Box>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={currentTab}
          onChange={(e, newValue) => setCurrentTab(newValue)}
          indicatorColor="primary"
          textColor="primary"
          variant={isMobile ? 'scrollable' : 'standard'}
          scrollButtons={isMobile ? 'auto' : false}
          allowScrollButtonsMobile
        >
          <Tab
            label={isMobile ? 'Pendientes' : 'Renovaciones Pendientes'}
            icon={<ScheduleIcon />}
            iconPosition="start"
            sx={{ minWidth: isMobile ? 'auto' : undefined }}
          />
          <Tab
            label={isMobile ? 'Historial' : 'Historial de Renovaciones'}
            icon={<HistoryIcon />}
            iconPosition="start"
            sx={{ minWidth: isMobile ? 'auto' : undefined }}
          />
          <Tab
            label={isMobile ? 'Canceladas' : 'Renovaciones Canceladas'}
            icon={<CloseIcon />}
            iconPosition="start"
            sx={{ minWidth: isMobile ? 'auto' : undefined }}
          />
          <Tab
            label={isMobile ? 'Proyecciones' : 'Proyecciones y Forecast'}
            icon={<TrendingUpIcon />}
            iconPosition="start"
            sx={{ minWidth: isMobile ? 'auto' : undefined }}
          />
        </Tabs>
      </Paper>

      {/* Tab Panel - Renovaciones Pendientes */}
      {currentTab === 0 && (
        <>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Card sx={{ border: statusCounts.overdue > 0 ? '2px solid' : 'none', borderColor: 'error.main' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="error.main" sx={{ fontWeight: 600 }}>
                {statusCounts.overdue}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Vencidas
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="warning.main" sx={{ fontWeight: 600 }}>
                {statusCounts.pending}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Pendientes
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="info.main" sx={{ fontWeight: 600 }}>
                {statusCounts.contacted}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Contactados
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="warning.main" sx={{ fontWeight: 600 }}>
                {statusCounts.paymentPending}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Pago Pendiente
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="success.main" sx={{ fontWeight: 600 }}>
                {statusCounts.completed}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Completadas
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 2 }}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" color="primary.main" sx={{ fontWeight: 600 }}>
                {statusCounts.total}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Total
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Estado</InputLabel>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              label="Estado"
            >
              <MenuItem value="all">Todos</MenuItem>
              <MenuItem value="pending">Pendientes</MenuItem>
              <MenuItem value="contacted">Contactados</MenuItem>
              <MenuItem value="payment_pending">Pago Pendiente</MenuItem>
              <MenuItem value="completed">Completadas</MenuItem>
              <MenuItem value="declined">Rechazadas</MenuItem>
              <MenuItem value="expired">Expiradas</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Prioridad</InputLabel>
            <Select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              label="Prioridad"
            >
              <MenuItem value="all">Todas</MenuItem>
              <MenuItem value="urgent">Urgente</MenuItem>
              <MenuItem value="high">Alta</MenuItem>
              <MenuItem value="medium">Media</MenuItem>
              <MenuItem value="low">Baja</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Renewals Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Cliente / Contrato</TableCell>
                <TableCell>Prioridad</TableCell>
                <TableCell>Fecha Vencimiento</TableCell>
                <TableCell>Último Contacto</TableCell>
                <TableCell>Pago</TableCell>
                <TableCell width={60}>Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : !renewalsData?.renewals.length ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary">
                      No hay renovaciones que coincidan con los filtros
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (() => {
                const groupedRenewals = groupRenewalsByClient(renewalsData.renewals);

                const rows: Array<{type: 'grouped', data: GroupedRenewal} | {type: 'single', data: RenewalWithDetails}> = [];

                groupedRenewals.forEach(group => {
                  if (group.count === 1) {
                    rows.push({type: 'single', data: group.renewals[0]});
                  } else {
                    rows.push({type: 'grouped', data: group});
                  }
                });

                if (rows.length !== groupedRowCount) {
                  setTimeout(() => setGroupedRowCount(rows.length), 0);
                }

                const paginatedRows = rows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

                return paginatedRows.map((row, index) => {
                  if (row.type === 'grouped') {
                    return (
                      <GroupedRenewalRow
                        key={`grouped-${row.data.clientId}`}
                        groupedRenewal={row.data}
                        onOpenDetails={handleOpenGroupedModal}
                      />
                    );
                  } else {
                    return (
                      <RenewalRow
                        key={row.data._id}
                        renewal={row.data}
                        onView={handleView}
                        onContact={handleContact}
                        onPayment={handleProcessRenewal}
                        onUpdateStatus={handleUpdateStatus}
                        onCancelRenewal={handleCancelRenewal}
                        onViewContactHistory={handleViewContactHistory}
                      />
                    );
                  }
                });
              })()}
            </TableBody>
          </Table>
        </TableContainer>

        {renewalsData && (
          <TablePagination
            rowsPerPageOptions={[10, 25, 50]}
            component="div"
            count={groupedRowCount || renewalsData.total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Filas por página:"
          />
        )}
      </Paper>

        </>
      )}

      {/* Tab Panel - Historial */}
      {currentTab === 1 && (
        <Paper>
          {loadingHistory ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Dispositivo</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Fecha Renovación</TableCell>
                    <TableCell>Próxima Expiración</TableCell>
                    <TableCell>Monto</TableCell>
                    <TableCell>Método Pago</TableCell>
                    <TableCell>Procesado Por</TableCell>
                    <TableCell>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {completedRenewals.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color="text.secondary">
                          No hay renovaciones completadas
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    completedRenewals.map((renewal) => (
                      <TableRow key={renewal._id} hover>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {renewal.deviceId?.imei || 'N/A'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {renewal.deviceId ? getDeviceBrandModel(renewal.deviceId) : 'N/A'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{renewal.clientId?.name || 'N/A'}</TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(renewal.completedDate)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="success.main">
                            {formatDate(renewal.newPeriod?.endDate)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {formatCurrency(renewal.paymentInfo?.amount || 0)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={renewal.paymentInfo?.paymentMethod || 'N/A'}
                            size="small"
                            color={renewal.paymentInfo?.paymentMethod === 'cash' ? 'default' : 'primary'}
                          />
                        </TableCell>
                        <TableCell>
                          {renewal.paymentInfo?.verifiedBy?.firstName && renewal.paymentInfo?.verifiedBy?.lastName
                            ? `${renewal.paymentInfo.verifiedBy.firstName} ${renewal.paymentInfo.verifiedBy.lastName}`
                            : renewal.paymentInfo?.verifiedBy?.email
                            ? renewal.paymentInfo.verifiedBy.email
                            : renewal.adminInfo?.assignedTo?.firstName && renewal.adminInfo?.assignedTo?.lastName
                            ? `${renewal.adminInfo.assignedTo.firstName} ${renewal.adminInfo.assignedTo.lastName}`
                            : 'N/A'}
                        </TableCell>
                        <TableCell>
                          <Tooltip title="Ver comprobante">
                            <span>
                              <IconButton
                                size="small"
                                disabled={!renewal.paymentInfo?.evidences?.length}
                                onClick={() => { const u = urlDeArchivo(renewal.paymentInfo?.evidences?.[0]?.url); if (u) window.open(u, '_blank'); }}
                              >
                                <ReceiptIcon />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* Tab Panel - Renovaciones Canceladas */}
      {currentTab === 2 && (
        <Paper>
          {loadingDeclined ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Dispositivo</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Fecha Cancelación</TableCell>
                    <TableCell>Razón</TableCell>
                    <TableCell>Días Vencido/Restante</TableCell>
                    <TableCell>Traccar</TableCell>
                    <TableCell>Simbase</TableCell>
                    <TableCell>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {declinedRenewals.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                        <Typography color="text.secondary">
                          No hay renovaciones canceladas
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    declinedRenewals.map((renewal) => (
                      <TableRow key={renewal._id} hover>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {renewal.deviceId?.imei || 'N/A'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {renewal.deviceId ? getDeviceBrandModel(renewal.deviceId) : 'N/A'}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>{renewal.clientId?.name || 'N/A'}</TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(renewal.cancellationInfo?.cancelledDate)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Por: {renewal.cancellationInfo?.cancelledBy?.firstName} {renewal.cancellationInfo?.cancelledBy?.lastName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {renewal.cancellationInfo?.reason || 'No especificada'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            color={renewal.isExpired ? 'error' : 'success.main'}
                            sx={{ fontWeight: 500 }}
                          >
                            {renewal.isExpired
                              ? `Vencido hace ${Math.abs(renewal.daysToExpiration)} días`
                              : `${renewal.daysToExpiration} días restantes`
                            }
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={renewal.cancellationInfo?.traccarDisabled ? 'Deshabilitado' : 'Error'}
                            size="small"
                            color={renewal.cancellationInfo?.traccarDisabled ? 'success' : 'error'}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={renewal.cancellationInfo?.simbaseDisabled ? 'Deshabilitado' : 'Error'}
                            size="small"
                            color={renewal.cancellationInfo?.simbaseDisabled ? 'success' : 'error'}
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="contained"
                            size="small"
                            color="primary"
                            startIcon={<CheckIcon />}
                            onClick={() => handleReactivateRenewal(renewal)}
                          >
                            Reactivar
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      {/* Tab Panel - Proyecciones */}
      {currentTab === 3 && (
        <Box>
          {loadingForecast ? (
            <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
          ) : forecastData ? (
            <>
              {/* KPIs de proyecciones */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="error.main" sx={{ fontWeight: 600 }}>
                        {forecastData.summary.totalOverdue}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Vencidos sin renovar</Typography>
                      <Typography variant="caption" color="error.main">
                        {formatCurrency(forecastData.summary.overdueRevenueLost)} perdidos
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="warning.main" sx={{ fontWeight: 600 }}>
                        {forecastData.summary.totalUpcoming}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Por expirar</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Potencial: {formatCurrency(forecastData.summary.potentialRevenue)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="success.main" sx={{ fontWeight: 600 }}>
                        {forecastData.summary.renewalRate}%
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Tasa de renovacion</Typography>
                      <LinearProgress
                        variant="determinate"
                        value={forecastData.summary.renewalRate}
                        color={forecastData.summary.renewalRate >= 50 ? 'success' : forecastData.summary.renewalRate >= 30 ? 'warning' : 'error'}
                        sx={{ mt: 1, height: 8, borderRadius: 4 }}
                      />
                    </CardContent>
                  </Card>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="h4" color="primary.main" sx={{ fontWeight: 600 }}>
                        {formatCurrency(forecastData.summary.projectedAnnualRevenue)}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">Ingreso proyectado</Typography>
                      <Typography variant="caption" color="text.secondary">
                        Basado en tasa del {forecastData.summary.renewalRate}%
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Vencidos por mes */}
              {forecastData.overdue.length > 0 && (
                <Paper sx={{ p: 3, mb: 3, border: '2px solid', borderColor: 'error.200' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: 'error.main' }}>
                    Vencidos sin renovar ({forecastData.summary.totalOverdue})
                  </Typography>
                  {forecastData.overdue.map((month: any) => (
                    <Accordion key={month.key}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 500, textTransform: 'capitalize' }}>
                            {month.label}
                          </Typography>
                          <Chip label={`${month.count} dispositivos`} size="small" color="error" />
                          <Typography variant="body2" color="error.main" sx={{ ml: 'auto', mr: 2 }}>
                            {formatCurrency(month.count * 2500)} perdidos
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Dispositivo</TableCell>
                              <TableCell>Cliente</TableCell>
                              <TableCell>Dias vencido</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {month.devices.map((d: any, i: number) => (
                              <TableRow key={i}>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{d.name}</Typography>
                                  <Typography variant="caption" color="text.secondary">{d.imei}</Typography>
                                </TableCell>
                                <TableCell>{d.client}</TableCell>
                                <TableCell>
                                  <Typography variant="body2" color="error.main" sx={{ fontWeight: 600 }}>
                                    {d.daysOverdue} dias
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Paper>
              )}

              {/* Proximas expiraciones por mes */}
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Expiraciones futuras por mes ({forecastData.summary.totalUpcoming})
                </Typography>
                {forecastData.upcoming.length === 0 ? (
                  <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                    No hay expiraciones programadas
                  </Typography>
                ) : (
                  forecastData.upcoming.map((month: any) => (
                    <Accordion key={month.key}>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 500, textTransform: 'capitalize' }}>
                            {month.label}
                          </Typography>
                          <Chip label={`${month.count} dispositivos`} size="small" color="primary" variant="outlined" />
                          <Typography variant="body2" color="success.main" sx={{ ml: 'auto', mr: 2 }}>
                            Potencial: {formatCurrency(month.revenue)}
                          </Typography>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Dispositivo</TableCell>
                              <TableCell>Cliente</TableCell>
                              <TableCell>Tipo</TableCell>
                              <TableCell>Dias restantes</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {month.devices.map((d: any, i: number) => (
                              <TableRow key={i}>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{d.name}</Typography>
                                  <Typography variant="caption" color="text.secondary">{d.imei}</Typography>
                                </TableCell>
                                <TableCell>{d.client}</TableCell>
                                <TableCell>
                                  <Chip label={d.clientType === 'dealer' ? 'Dealer' : 'Individual'} size="small" variant="outlined" />
                                </TableCell>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    sx={{ fontWeight: 500 }}
                                    color={d.daysUntil <= 30 ? 'warning.main' : 'text.primary'}
                                  >
                                    {d.daysUntil} dias
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </AccordionDetails>
                    </Accordion>
                  ))
                )}
              </Paper>
            </>
          ) : (
            <Alert severity="error">Error cargando proyecciones</Alert>
          )}
        </Box>
      )}

      {/* Process Renewal Dialog */}
      <ProcessRenewalDialog
        open={processRenewalOpen}
        renewal={selectedRenewal}
        onClose={() => setProcessRenewalOpen(false)}
        onSuccess={handleRenewalSuccess}
      />

      {/* Payment Dialog - Mantener por compatibilidad */}
      <PaymentDialog
        open={paymentDialogOpen}
        renewal={selectedRenewal}
        onClose={() => setPaymentDialogOpen(false)}
        onSubmit={handlePaymentSubmit}
        loading={uploadingPayment}
      />

      {/* Contact Log Dialog */}
      <ContactLogDialog
        open={contactDialogOpen}
        onClose={() => {
          setContactDialogOpen(false);
          // Refrescar datos después de cerrar el diálogo de contacto
          fetchRenewals();
        }}
        renewalId={selectedRenewal?._id || ''}
        clientName={selectedRenewal?.clientId?.name || ''}
        clientEmail={selectedRenewal?.clientId?.email || ''}
        deviceImei={selectedRenewal?.deviceId?.imei || ''}
      />

      {/* Contact History Modal */}
      <ContactHistoryModal
        open={contactHistoryModalOpen}
        renewal={selectedRenewal}
        onClose={() => setContactHistoryModalOpen(false)}
        onAddContact={(renewal) => {
          setSelectedRenewal(renewal);
          setContactDialogOpen(true);
        }}
      />

      {/* Details Dialog */}
      <DetailsDialog
        open={detailsDialogOpen}
        renewal={selectedRenewal}
        onClose={() => setDetailsDialogOpen(false)}
        onCreateRenewal={handleProcessRenewal}
      />

      {/* Grouped Renewals Modal */}
      <GroupedRenewalsModal
        open={groupedModalOpen}
        groupedRenewal={selectedGroupedRenewal}
        onClose={() => setGroupedModalOpen(false)}
        onProcessRenewal={handleProcessRenewal}
        onContact={handleContact}
        onCancel={handleCancelRenewal}
      />

      {/* Success Message */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSuccessMessage('')} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RenewalsPage;